import { useMemo, useState } from 'react';

import { Grid, GridItem, HStack } from '@chakra-ui/react';

import { useQueries } from '@tanstack/react-query';

import { PaginationState, SortingState } from '@tanstack/react-table';

import { Table } from 'app/components/UI/Table/Table';

import { defaultPageSize } from 'app/constants/table';

import getDeductionRequestsColumns from 'app/utils/table/deductionRequests';

import ProfileSection from 'app/components/UI/ProfileSection';

import ProfileField from 'app/components/UI/ProfileField';

import useFetchDeductionRequests from 'app/hooks/useFetchDeductionRequests';

import { format, parseISO } from 'date-fns';

import { formatCurrency } from 'app/utils/strings';

import {
  DEFAULT_DATE_FORMAT,
  DeductionRequest,
  PayCalendar,
  payCalendarsService,
  PayCycle,
  PayFrequency,
} from '@aslanpay/portal-common-lib';

import { DeductionRequestTypeLabels } from 'app/constants/deductionRequests';

import IndividualSavingsPots from '../IndividualSavingsPots';

import { DeductionRequestTableRow, IndividualDeductionRequestsTabProps } from './types';

// A deduction request's period must be resolved against the calendar it was actually created
// under (request.payCalendarId), not the individual's current calendar — a worker who has since
// been reassigned to a different pay calendar still has older requests referencing cycles that
// only exist on their previous calendar.
export const formatDeductionRequestPeriod = (
  payCycle: PayCycle | undefined,
  payFrequency: PayFrequency | undefined
) => {
  if (!payCycle?.startDate) {
    return '';
  }

  if (payFrequency === 'MONTHLY') {
    return format(parseISO(payCycle.startDate), 'MMMM yyyy');
  }

  if (!payCycle.endDate) {
    return format(parseISO(payCycle.startDate), DEFAULT_DATE_FORMAT);
  }

  return `${format(parseISO(payCycle.startDate), DEFAULT_DATE_FORMAT)} - ${format(
    parseISO(payCycle.endDate),
    DEFAULT_DATE_FORMAT
  )}`;
};

const sumAmounts = (requests?: DeductionRequest[]) =>
  (requests || []).reduce((sum, request) => sum + request.amount, 0);

function IndividualDeductionRequestsTab({
  individual,
  payCalendar,
  type,
}: IndividualDeductionRequestsTabProps) {
  const typeLabel = DeductionRequestTypeLabels[type];
  const currency = payCalendar ? payCalendar.currency : 'GBP';

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: deductionRequests } = useFetchDeductionRequests(
    {
      individualIds: [individual?.id || ''],
      status: ['REQUESTED', 'PENDING', 'AVAILABLE', 'ACTION_REQUIRED', 'COMPLETED', 'CANCELLED'],
      type: [type],
      page: pageIndex,
      size: pageSize,
      sortBy: sorting[0]?.id,
      sort: !sorting[0] || sorting[0]?.desc ? 'DESC' : 'ASC',
    },
    type === 'RESERVE'
  );

  const { data: pendingDeductionRequests } = useFetchDeductionRequests({
    individualIds: [individual?.id || ''],
    status: ['REQUESTED', 'PENDING'],
    type: [type],
    page: 0,
    size: 99999,
  });

  const { data: completedDeductionRequests } = useFetchDeductionRequests({
    individualIds: [individual?.id || ''],
    status: ['COMPLETED'],
    type: [type],
    page: 0,
    size: 99999,
  });

  const totalCompletedDeduction = sumAmounts(completedDeductionRequests?.list);
  const totalPendingDeduction = sumAmounts(pendingDeductionRequests?.list);

  // Older deduction requests can reference a pay calendar the worker has since been reassigned
  // away from — fetch every distinct calendar actually referenced by the fetched requests instead
  // of assuming they all belong to the individual's current one.
  const distinctPayCalendarIds = Array.from(
    new Set((deductionRequests?.list || []).map((request) => request.payCalendarId).filter(Boolean))
  );

  const payCalendarQueries = useQueries({
    queries: distinctPayCalendarIds.map((payCalendarId) => ({
      queryKey: ['fetchPayCalendarForDeductionRequest', payCalendarId],
      queryFn: () => payCalendarsService.fetchPayCalendar(payCalendarId),
      enabled: !!payCalendarId,
    })),
  });

  const payCalendarById = useMemo(() => {
    const map = new Map<string, PayCalendar>();
    if (payCalendar) {
      map.set(payCalendar.id, payCalendar);
    }
    payCalendarQueries.forEach((query) => {
      if (query.data) {
        map.set(query.data.id, query.data);
      }
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payCalendar, JSON.stringify(payCalendarQueries.map((query) => query.data?.id))]);

  const updatedDeductionRequests = deductionRequests?.list.map((request) => {
    const requestPayCalendar = payCalendarById.get(request.payCalendarId) || payCalendar;
    const payCycle = requestPayCalendar?.payCycles.find((cycle) => cycle.id === request.payCycleId);

    return {
      ...request,
      period: formatDeductionRequestPeriod(payCycle, requestPayCalendar?.payFrequency),
      currency: requestPayCalendar?.currency || payCalendar?.currency,
    };
  });

  const renderGeneralDetails = () => (
    <GridItem display="flex">
      <ProfileSection title={`${typeLabel} summary`} maxW="full">
        <HStack spacing="20px" align="flex-start">
          <ProfileField
            label="Global Percentage Allowance (%)"
            value={`${
              individual?.globalAllowancePercentage ?? payCalendar?.globalAllowancePercentage ?? 0
            } %`}
          />
          <ProfileField
            label={`Total ${typeLabel} to date`}
            value={formatCurrency(totalCompletedDeduction, currency, 'en-GB')}
          />
        </HStack>
        <HStack spacing="20px" align="flex-start">
          <ProfileField
            label={`Pending ${typeLabel} requests`}
            value={formatCurrency(totalPendingDeduction, currency, 'en-GB')}
          />
        </HStack>
      </ProfileSection>
    </GridItem>
  );

  const renderDeductionRequestsTable = () => (
    <GridItem display="flex">
      <ProfileSection title={`${typeLabel} history`} maxW="full">
        <Table
          columns={getDeductionRequestsColumns()}
          data={(updatedDeductionRequests as DeductionRequestTableRow[]) || []}
          total={deductionRequests?.total || 0}
          pageNumber={deductionRequests?.pageNumber || pageIndex}
          pageSize={deductionRequests?.pageSize || pageSize}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      </ProfileSection>
    </GridItem>
  );

  const renderSavingsPots = () => (
    <GridItem display="flex">
      <ProfileSection title="Savings pots" maxW="full">
        <IndividualSavingsPots individual={individual} payCalendar={payCalendar} />
      </ProfileSection>
    </GridItem>
  );

  return (
    <Grid w="full" templateColumns="minmax(0, 1fr)" gap="25px">
      {renderGeneralDetails()}
      {type === 'SAVINGS' ? renderSavingsPots() : renderDeductionRequestsTable()}
    </Grid>
  );
}

export default IndividualDeductionRequestsTab;
